import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, ScrollText, Trash2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useModuleAccess } from '../auth/useModuleAccess';
import { hasSupabaseConfig } from '../lib/supabaseClient';
import { formatSupabaseError } from '../lib/supabaseErrors';
import {
  deleteMeetingArchive,
  exportArchiveMinutes,
  fetchMeetingArchive,
  listMeetingArchives,
} from '../utils/meetingArchives';
import SignedInUserLine from '../components/common/SignedInUserLine';

function formatEnded(row) {
  if (!row?.ended_at) return '—';
  return new Date(row.ended_at).toLocaleString();
}

export default function MeetingLogPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { switchOrganization, ready, memberships } = useAuth();
  const { canMeeting, canAdmin } = useModuleAccess();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  const membershipForOrg = memberships.find((m) => m.organization_id === orgId);
  const allowed = canMeeting || canAdmin;

  useEffect(() => {
    if (!ready) return;
    if (!hasSupabaseConfig()) {
      navigate('/', { replace: true });
      return;
    }
    if (orgId) void switchOrganization(orgId);
  }, [ready, orgId, switchOrganization, navigate]);

  useEffect(() => {
    if (ready && orgId && membershipForOrg && !allowed) {
      navigate(`/orgs/${orgId}`, { replace: true });
    }
  }, [ready, orgId, membershipForOrg, allowed, navigate]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setBusy(true);
    setError(null);
    try {
      const list = await listMeetingArchives(orgId);
      setRows(list);
    } catch (err) {
      console.error('[Meeting Log] list failed', err);
      setError(formatSupabaseError(err, 'Failed to load Meeting Log'));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (allowed && orgId && membershipForOrg) void load();
  }, [allowed, orgId, membershipForOrg, load]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailBusy(true);
    void fetchMeetingArchive(selectedId)
      .then((row) => {
        if (!cancelled) setDetail(row);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(formatSupabaseError(err, 'Failed to load meeting'));
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const onExport = (format) => {
    if (!detail) return;
    try {
      exportArchiveMinutes(detail, format);
    } catch (err) {
      setError(formatSupabaseError(err, 'Export failed'));
    }
  };

  const openDelete = (row) => {
    setDeleteTarget(row);
    setDeleteConfirmText('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== 'DELETE') return;
    setDeleteBusy(true);
    setError(null);
    try {
      await deleteMeetingArchive(deleteTarget.id);
      if (selectedId === deleteTarget.id) setSelectedId(null);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      await load();
    } catch (err) {
      setError(formatSupabaseError(err, 'Could not delete meeting'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const orgName = membershipForOrg?.organization_name || 'Organization';

  return (
    <div className="min-h-screen bg-pp-paper text-pp-ink">
      <header className="border-b border-pp-line/80 bg-pp-surface/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <Link
              to={`/orgs/${orgId}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-pp-muted hover:text-pp-ink"
            >
              <ArrowLeft className="h-4 w-4" />
              Org home
            </Link>
            <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold tracking-tight">
              <ScrollText className="h-5 w-5 text-pp-accent" aria-hidden />
              Meeting Log
            </h1>
            <p className="text-sm text-pp-muted">{orgName}</p>
            <SignedInUserLine className="mt-0.5" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {error && (
          <p className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {error}
          </p>
        )}

        {selectedId ? (
          <div>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="mb-4 text-sm font-medium text-pp-muted hover:text-pp-ink"
            >
              ← Back to list
            </button>
            {detailBusy || !detail ? (
              <p className="text-sm text-pp-muted">Loading…</p>
            ) : (
              <div className="rounded-2xl border border-pp-line bg-pp-surface p-5 shadow-pp">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight">{detail.title}</h2>
                    <p className="mt-1 text-sm text-pp-muted">
                      {detail.ended_at
                        ? new Date(detail.ended_at).toLocaleString()
                        : '—'}
                      {detail.present_members != null && detail.total_members != null
                        ? ` · ${detail.present_members}/${detail.total_members} present`
                        : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onExport('pdf')}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-pp-accent px-3 py-2 text-sm font-semibold text-white hover:bg-pp-accentHover"
                    >
                      <Download className="h-4 w-4" />
                      PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => onExport('doc')}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-pp-line bg-white px-3 py-2 text-sm font-semibold text-pp-ink hover:border-pp-accent/40"
                    >
                      <FileText className="h-4 w-4" />
                      Word
                    </button>
                    {canAdmin && (
                      <button
                        type="button"
                        onClick={() => openDelete(detail)}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-100"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <ul className="mt-6 max-h-[min(70vh,40rem)] space-y-3 overflow-y-auto border-t border-pp-line pt-4">
                  {(Array.isArray(detail.minutes) ? detail.minutes : [])
                    .slice()
                    .sort((a, b) => (b.chronologicalOrder || 0) - (a.chronologicalOrder || 0))
                    .map((entry) => (
                      <li
                        key={entry.id || `${entry.timestamp}-${entry.text}`}
                        className={`rounded-xl border px-3 py-2.5 text-sm ${
                          entry.activityOnly
                            ? 'border-pp-line/60 bg-pp-paper/80 text-pp-muted'
                            : 'border-pp-line bg-white text-pp-ink'
                        }`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide text-pp-muted">
                            {entry.type || 'entry'}
                          </span>
                          <span className="text-xs text-pp-muted">{entry.timestamp}</span>
                        </div>
                        <p className="mt-1 leading-snug">{entry.text}</p>
                        {entry.result ? (
                          <p className="mt-1 text-xs font-medium text-pp-sage">Result: {entry.result}</p>
                        ) : null}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="mb-6 text-sm text-pp-muted">
              Past meetings saved when you end a live session or start a new meeting. Open an entry to
              review or re-export minutes.
            </p>
            {busy ? (
              <p className="text-sm text-pp-muted">Loading…</p>
            ) : rows.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-pp-line bg-pp-surface px-6 py-12 text-center">
                <FileText className="mx-auto h-8 w-8 text-pp-muted" aria-hidden />
                <p className="mt-3 text-sm font-medium text-pp-ink">No meetings in the log yet</p>
                <p className="mt-1 text-sm text-pp-muted">
                  Run a meeting and end the live session—or start a new meeting after recording
                  minutes—to save an entry here.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/orgs/${orgId}/meeting`)}
                  className="mt-6 rounded-xl bg-pp-accent px-4 py-2 text-sm font-semibold text-white"
                >
                  Start a meeting
                </button>
              </div>
            ) : (
              <ul className="space-y-2">
                {rows.map((row) => (
                  <li key={row.id} className="flex items-stretch gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-2xl border border-pp-line bg-pp-surface px-4 py-3.5 text-left shadow-pp hover:border-pp-accent/40"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-semibold tracking-tight text-pp-ink">{row.title}</p>
                        <p className="mt-0.5 text-xs text-pp-muted">{formatEnded(row)}</p>
                      </div>
                      <span className="shrink-0 text-sm font-medium text-pp-accent">Open</span>
                    </button>
                    {canAdmin && (
                      <button
                        type="button"
                        title="Delete meeting"
                        onClick={() => openDelete(row)}
                        className="shrink-0 rounded-2xl border border-rose-200 bg-rose-50 px-3 text-rose-800 hover:bg-rose-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>

      {deleteTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-meeting-title"
            className="w-full max-w-md rounded-2xl border border-pp-line bg-pp-surface p-5 shadow-pp"
          >
            <h2 id="delete-meeting-title" className="text-lg font-semibold text-pp-ink">
              Delete meeting permanently?
            </h2>
            <p className="mt-2 text-sm text-pp-muted">
              This removes the Meeting Log entry and cannot be undone from the app.
            </p>
            <p className="mt-3 rounded-lg bg-pp-paper px-3 py-2 text-sm font-medium text-pp-ink">
              {deleteTarget.title || 'Untitled meeting'}
              <span className="mt-0.5 block text-xs font-normal text-pp-muted">
                {formatEnded(deleteTarget)}
              </span>
            </p>
            <label className="mt-4 block text-xs font-medium text-pp-muted">
              Type <span className="font-mono text-pp-ink">DELETE</span> to confirm
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteConfirmText('');
                }}
                className="rounded-xl border border-pp-line px-4 py-2 text-sm font-medium text-pp-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteConfirmText !== 'DELETE' || deleteBusy}
                onClick={() => void confirmDelete()}
                className="rounded-xl bg-rose-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                {deleteBusy ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
